import { useMemo, useEffect } from 'react';

import TeachingNotesDetailRichtextView from './teaching-notes-detail-richtext-view';
import {
  LEARNING_SEQUENCE_ITEM_TYPE_QUESTION,
  LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB,
  LESSON_ACTIVITIES,
} from 'global/constants';
import TeachingNotesListView from './teaching-notes-list-view';
import { PStyled } from 'approot/shared/styles/typography.style';
import {
  getExplanationNote,
  getLevelOfAttainmentNote,
  getNotes,
  scrollQuestionIntoView,
} from './teaching-notes.utils';
import {
  TeachingNotesDrawerContainer,
  TeachingNotesDuration,
  TeachingNotesSection,
  TeachingNotesSectionLabel,
} from './teaching-notes-drawer.style';
import Accordion from 'approot/shared/accordion/accordion';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';
import { ComponentActivity } from '../component.constants';
import { useGetLessonSequences } from './teaching-notes.hooks';
import { getCategoryOptions } from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.utils';
import TeachingNotesTheEs from './teaching-notes-theEs-view';
import TeachingNotesMetaDataItem from './teaching-notes-meta-data-item';
import { grey7 } from 'approot/shared/styles/colours.style';
import { TeachingLearningSequenceLink } from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.style';
import { getDifferentiationHeading } from 'approot/shared/differentiation/differentiation.utils';
import { isDifferentiatedAssessmentActivity } from 'approot/shared/logic/is-differentiated-assessment-activity';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import TooltipInfo from 'approot/shared/tooltip/tooltip-info';
import { getDifferentiationDescription } from '../activity/activity-view-details.utils';
import { ValidSpacings, spacing16 } from 'approot/shared/styles/spacing.style';
import {
  BlueSquare,
  GreenCircle,
  PurpleCircle,
} from 'approot/shared/differentiation/differentiation.style';
import BlackDiamond from 'approot/shared/differentiation/black-diamond';
import { FlexLeftAlign } from 'approot/shared/styles/flex.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  TeachingLearningSequenceCheckpointModal,
  TeachingLearningSequenceEnrichmentModal,
} from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence-modal';
import TeachingLearningSequenceCheckpointButtons from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence-buttons';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { getDifferentiationBlogLink } from 'approot/shared/logic/get-differentiation-blog-link';
import { Box } from 'approot/shared/design-system/box/box.style';
import SuggestedAnswersView from '../suggested-answers/view/suggested-answer-view';
import { useGetActivitySections } from 'approot/activity-builder/activity-builder-section.hooks';
import TeachingNotesActivityPartsView from './teaching-notes-activity-parts-view';
import { isRichtextValueEmpty } from '../shared/logic/get-richtext-value';
import { getActivityPartsSequenceItems } from 'approot/shared/teaching-learning-sequence/logic/get-activity-parts-sequence-items';
import { useDifferentiationTranslation } from 'approot/shared/differentiation/use-differentiation-translation';
import TeachingNotesActivityOutcomes from './view/teaching-notes-activity-outcomes';
import OrangeCross from 'approot/shared/differentiation/orange-cross';

type Props = {
  contentId: number;
  classId?: number;
  version: string | undefined;
  activityNumber: number;
  activity: ComponentActivity;
  urlSectionId: string;
  isPublic: boolean;
};

const TeachingNotesDetailQuestionView = ({
  contentId,
  classId,
  version,
  activityNumber,
  activity,
  urlSectionId,
  isPublic,
}: Props) => {
  const { region, isRegionAU } = useRegionData();
  const { unitQuizDescription } = useDifferentiationTranslation();

  const {
    isVisible: isEnrichmentModalVisible,
    openModal: openEnrichmentModal,
    closeModal: closeEnrichmentModal,
  } = useSimpleModalState();
  const {
    isVisible: isCheckpointModalVisible,
    openModal: openCheckpointModal,
    closeModal: closeCheckpointModal,
  } = useSimpleModalState();

  const { sequences } = useGetLessonSequences({
    contentId,
    classId,
    version,
    authenticate: !isPublic,
  });

  useGetActivitySections({
    contentId,
    contentType: LESSON_ACTIVITIES,
    classId,
    sectionIds: [activity.sectionId],
    skip: !activity.sectionId,
    isPublic,
    version,
  });

  const explanation = useMemo(
    () => getExplanationNote(sequences, activity.id),
    [sequences, activity.id]
  );

  const activityParts = useMemo(
    () => getActivityPartsSequenceItems(sequences, activity.id),
    [sequences, activity.id]
  );

  const hasActivityPartsData = useMemo(
    () => activityParts.some(ad => !isRichtextValueEmpty(ad.data)),
    [activityParts]
  );

  const mainActivityPart = useMemo(
    () => activityParts.find(d => d.sectionId === activity.sectionId),
    [activity.sectionId, activityParts]
  );

  const notes = useMemo(
    () =>
      getNotes(sequences, activity.id, LEARNING_SEQUENCE_ITEM_TYPE_QUESTION),
    [sequences, activity.id]
  );

  const levelOfAttainment = useMemo(
    () => getLevelOfAttainmentNote(sequences, activity.id),
    [sequences, activity.id]
  );

  const categoryOptions = useMemo(
    () =>
      getCategoryOptions({
        region,
        categories: LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB,
      }),
    [region]
  );

  useEffect(() => {
    scrollQuestionIntoView(activity.id);
  }, [activity.id]);

  if (!activity) return null;

  const isCheckPoint = !!activity.properties.activitySettings.checkPoint;
  const isFurtherEnrichment =
    !!activity.properties.activitySettings.furtherEnrichment;
  const hasExplanationData = !isRichtextEmpty(mainActivityPart?.data);
  const hasEs = !!mainActivityPart?.theEsDetails?.length;
  const hasThinkingActivities =
    !!mainActivityPart?.visibleThinkingActivitiesDetails?.length;
  const hasHighImpact =
    !!mainActivityPart?.highImpactTeachingStrategiesDetails?.length;
  const duration = activity.properties.activitySettings.duration;
  const differentiation =
    activity.properties.activitySettings.differentiation ||
    Differentiation.GreenCircle;

  if (!sequences) return null;

  return (
    <TeachingNotesDrawerContainer>
      <TeachingNotesSection>
        <Box $marginBottom={4}>
          <FlexLeftAlign>
            <TeachingNotesDifferentiation
              differentiation={differentiation}
              marginRight={8}
            />{' '}
            <TooltipInfo
              hoverText={getDifferentiationDescription(
                differentiation,
                unitQuizDescription
              )}
              multiline
            />
          </FlexLeftAlign>
        </Box>

        {!isDifferentiatedAssessmentActivity(differentiation) && (
          <Box $marginBottom={isCheckPoint || isFurtherEnrichment ? 16 : 0}>
            <TeachingLearningSequenceLink
              href={getRegionalisedLink(
                getDifferentiationBlogLink({ isRegionAU })
              )}
              target="_blank"
            >
              Learn about Differentiation with Inquisitive
            </TeachingLearningSequenceLink>
          </Box>
        )}

        <TeachingLearningSequenceCheckpointButtons
          isCheckPoint={isCheckPoint}
          isFurtherEnrichment={isFurtherEnrichment}
          onClickCheckPoint={openCheckpointModal}
          onClickFurtherEnrichment={openEnrichmentModal}
          marginBottom={0}
          gap={spacing16}
        />
      </TeachingNotesSection>

      {!!duration && (
        <TeachingNotesDuration>
          <TeachingNotesSectionLabel marginBottom={0}>
            Time
          </TeachingNotesSectionLabel>
          <PStyled
            colour={grey7}
            marginBottom={0}
            data-selector="activity-duration"
          >
            {duration} mins
          </PStyled>
        </TeachingNotesDuration>
      )}

      <Accordion
        id={`question-${activityNumber}-accordion`}
        key={`question-${activityNumber}-accordion`}
      >
        {isCheckPoint && (
          <TeachingNotesDetailRichtextView
            data={levelOfAttainment?.data}
            title="Level of student attainment &amp; observations of student performance"
          />
        )}
        {hasActivityPartsData ? (
          <TeachingNotesActivityPartsView
            sectionId={activity.sectionId}
            urlSectionId={urlSectionId}
            contentId={contentId}
            contentType={LESSON_ACTIVITIES}
            classId={classId ?? 0}
            component={activity}
            sequences={sequences}
            version={version}
          />
        ) : explanation ? (
          <TeachingNotesDetailRichtextView
            data={explanation.data}
            title="Teaching Notes"
            displayExpanded
          />
        ) : null}

        {mainActivityPart?.activityOutcomes?.outcomes &&
        mainActivityPart.activityOutcomes.outcomes.length > 0 ? (
          <TeachingNotesActivityOutcomes
            outcomes={mainActivityPart?.activityOutcomes}
          />
        ) : explanation?.activityOutcomes &&
          explanation.activityOutcomes.outcomes.length > 0 ? (
          <TeachingNotesActivityOutcomes
            outcomes={explanation.activityOutcomes}
          />
        ) : null}

        {mainActivityPart && (
          <>
            <SuggestedAnswersView
              contentId={contentId}
              contentType={LESSON_ACTIVITIES}
              currentSectionId={activity.sectionId}
              urlSectionId={urlSectionId}
              version={version}
              isPublic={isPublic}
              classId={classId ?? null}
            />
            {hasEs && (
              <TeachingNotesTheEs
                theEsDetails={mainActivityPart.theEsDetails!}
                displayExpanded={!hasExplanationData}
              />
            )}
            {hasThinkingActivities && (
              <TeachingNotesMetaDataItem
                title="Visible Thinking Activities"
                items={mainActivityPart.visibleThinkingActivitiesDetails!}
                displayExpanded={!hasExplanationData && !hasEs}
              />
            )}
            {hasHighImpact && (
              <TeachingNotesMetaDataItem
                title="High Impact Teaching Strategies"
                items={mainActivityPart.highImpactTeachingStrategiesDetails!}
                displayExpanded={
                  !hasExplanationData && !hasEs && !hasThinkingActivities
                }
              />
            )}
          </>
        )}

        <TeachingNotesListView
          notes={notes}
          categories={categoryOptions}
          displayExpanded={
            !hasExplanationData &&
            !hasEs &&
            !hasThinkingActivities &&
            !hasHighImpact
          }
        />
      </Accordion>

      {isEnrichmentModalVisible && (
        <TeachingLearningSequenceEnrichmentModal close={closeEnrichmentModal} />
      )}
      {isCheckpointModalVisible && (
        <TeachingLearningSequenceCheckpointModal close={closeCheckpointModal} />
      )}
    </TeachingNotesDrawerContainer>
  );
};

export const TeachingNotesDifferentiation = ({
  differentiation,
  marginRight,
}: {
  differentiation: Differentiation;
  marginRight?: ValidSpacings;
}) => {
  const { unitQuizLabel } = useDifferentiationTranslation();
  return (
    <>
      {differentiation === Differentiation.GreenCircle ? (
        <GreenCircle $marginRight={marginRight} $width={16} $height={16} />
      ) : differentiation === Differentiation.BlueSquare ? (
        <BlueSquare $marginRight={marginRight} $width={16} $height={16} />
      ) : differentiation === Differentiation.BlackDiamond ? (
        <BlackDiamond marginRight={marginRight} size={16} />
      ) : differentiation === Differentiation.OrangeCross ? (
        <OrangeCross marginRight={marginRight} size={16} />
      ) : (
        <PurpleCircle $marginRight={marginRight} $width={16} $height={16} />
      )}
      <TeachingNotesSectionLabel marginBottom={0}>
        {getDifferentiationHeading(differentiation, unitQuizLabel)} Activity
      </TeachingNotesSectionLabel>
    </>
  );
};

export default TeachingNotesDetailQuestionView;
